import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Button from "../components/inputs/Button";
import Input from "../components/inputs/Input";
import Textarea from "../components/shared/Textarea";
import { contactSchema } from "../types/schemas";

const Contact = () => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
    mode: "onChange",
  });

  const onSubmit = async (data) => {
    try {
      // Handle form submission here
      console.log("Form submitted:", data);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast.success("Message sent successfully!");
      reset();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    }
  };

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Contact</h1>

          <p className="text-gray-900 mb-8">
            Should you have any additional questions about the Scientific
            Research Panel that were not answered on this site, please contact:
          </p>

          {/* Contact Information */}
          <div className="mb-8">
            {/* First Contact Person */}
            <div className="mb-6">
              <div className="flex">
                <div className="w-1 bg-blue-500 mr-4"></div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Julia Madey
                  </h2>
                  <p className="text-gray-700 mb-2">Project Manager</p>
                  <p className="text-gray-700 mb-2">
                    <a
                      href="mailto:Jmadey@nibs.org"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Jmadey@nibs.org
                    </a>
                  </p>
                  <div className="text-gray-700">
                    <p>National Institute of Building Sciences (NIBS)</p>
                    <p>2121 K St NW, Suite 800</p>
                    <p>Washington, DC 20037</p>
                    <p>(610) 757-7861</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Separator */}
            <div className="text-center mb-6">
              <span className="text-gray-700">or</span>
            </div>

            {/* Second Contact Person */}
            <div className="mb-6">
              <div className="flex">
                <div className="w-1 bg-blue-500 mr-4"></div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Eric Rawdon
                  </h2>
                  <p className="text-gray-700 mb-2">Program Director</p>
                  <p className="text-gray-700 mb-2">
                    <a
                      href="mailto:Erawdon@nibs.org"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Erawdon@nibs.org
                    </a>
                  </p>
                  <div className="text-gray-700">
                    <p>National Institute of Building Sciences (NIBS)</p>
                    <p>2121 K St NW, Suite 800</p>
                    <p>Washington, DC 20037</p>
                    <p>(508) 769-4136</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Name and Email Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Name"
                      type="text"
                      name="name"
                      field={field}
                      error={errors.name}
                      required
                      placeholder="Your full name"
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Email"
                      type="email"
                      name="email"
                      field={field}
                      error={errors.email}
                      required
                      placeholder="your.email@example.com"
                    />
                  )}
                />
              </div>

              {/* Subject */}
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Subject"
                    type="text"
                    name="subject"
                    field={field}
                    error={errors.subject}
                    required
                    placeholder="Subject of your inquiry"
                  />
                )}
              />

              {/* Message */}
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <Textarea
                    label="Message"
                    name="message"
                    rows={6}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.message?.message}
                    required
                    placeholder="Your message here..."
                    maxLength={2000}
                    showCount
                  />
                )}
              />

              {/* Submit Button */}
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  className=""
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send message"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
